import React, { useEffect, useState } from "react";
import {
    Button, Dialog, DialogActions, DialogContent, FormControl, InputLabel,
    IconButton, MenuItem, Select, TextField, Tooltip
} from "@mui/material";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ClearIcon from '@mui/icons-material/Clear';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { toast } from "react-toastify";
import { URLS } from "../../helpers/URLS";
import useAxios from "../../helpers/Hooks/useAxios";
import '../../assets/scss/tables.scss';

const skillLevels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

const emptySkill = { name: "", level: "" };

const Fact = ({ icon, label, value }) => (
    <div className="wh-fact">
        <span className="wh-fact-icon">{icon}</span>
        <div className="wh-fact-text">
            <span className="wh-fact-label">{label}</span>
            <span className="wh-fact-value" title={value || ""}>{value || "--"}</span>
        </div>
    </div>
);

/* Shown the first time an employee signs in with the credentials mailed to
   them. Name, email and manager come from their record and cannot be changed
   here; experience, current skills and desired skills are all mandatory, so
   the dialog has no way out other than saving them. */
const ProfileSetupModal = ({ open, user = {}, onCompleted }) => {

    const { fetchData } = useAxios();

    const [experience, setExperience] = useState("");
    const [skills, setSkills] = useState([]);
    const [newSkill, setNewSkill] = useState(emptySkill);
    const [desiredSkills, setDesiredSkills] = useState([]);
    const [newDesiredSkill, setNewDesiredSkill] = useState("");
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    /* an employee can already have some of this on record — an admin may have
       filled the experience in, say — so start from whatever is there */
    useEffect(() => {
        if (!open) return;
        setExperience(user.experience || "");
        setSkills(Array.isArray(user.skills)
            ? user.skills.map(skill => ({ name: skill.name || "", level: skill.level || "" }))
                .filter(skill => skill.name && skill.level)
            : []);
        setDesiredSkills(Array.isArray(user.desiredSkills)
            ? user.desiredSkills.filter(skill => String(skill || "").trim())
            : []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, user.employeeCode]);

    const clearError = (field) => setErrors(prev => ({ ...prev, [field]: "" }));

    const addSkill = () => {
        const name = newSkill.name.trim();
        if (!name || !newSkill.level) {
            setErrors(prev => ({ ...prev, skills: "Enter a skill name and pick its level" }));
            return false;
        }
        if (skills.some(skill => skill.name.toLowerCase() === name.toLowerCase())) {
            setErrors(prev => ({ ...prev, skills: `${name} is already in the list` }));
            return false;
        }
        setSkills([...skills, { name, level: newSkill.level }]);
        setNewSkill(emptySkill);
        clearError('skills');
        return true;
    };

    const removeSkill = (index) => setSkills(skills.filter((_, i) => i !== index));

    const addDesiredSkill = () => {
        const name = newDesiredSkill.trim();
        if (!name) {
            setErrors(prev => ({ ...prev, desiredSkills: "Enter a skill you would like to work on" }));
            return false;
        }
        if (desiredSkills.some(skill => skill.toLowerCase() === name.toLowerCase())) {
            setErrors(prev => ({ ...prev, desiredSkills: `${name} is already in the list` }));
            return false;
        }
        setDesiredSkills([...desiredSkills, name]);
        setNewDesiredSkill("");
        clearError('desiredSkills');
        return true;
    };

    const removeDesiredSkill = (item) =>
        setDesiredSkills(desiredSkills.filter(skill => skill !== item));

    const submit = async () => {
        /* anything typed but not yet added is folded in, so a half-filled row
           is not silently dropped on save */
        let allSkills = skills;
        if (newSkill.name.trim() || newSkill.level) {
            if (!addSkill()) return;
            allSkills = [...skills, { name: newSkill.name.trim(), level: newSkill.level }];
        }

        let allDesiredSkills = desiredSkills;
        if (newDesiredSkill.trim()) {
            if (!addDesiredSkill()) return;
            allDesiredSkills = [...desiredSkills, newDesiredSkill.trim()];
        }

        const nextErrors = {
            experience: experience.trim() ? "" : "Experience is required",
            skills: allSkills.length ? "" : "Add at least one current skill",
            desiredSkills: allDesiredSkills.length ? "" : "Add at least one desired skill",
        };
        setErrors(nextErrors);
        if (Object.values(nextErrors).some(Boolean)) return;

        setSaving(true);
        const resp = await fetchData({
            url: URLS.completeProfile,
            method: 'post',
            body: {
                experience: experience.trim(),
                skills: allSkills,
                desiredSkills: allDesiredSkills,
            }
        });
        setSaving(false);

        if (resp && resp.status === 200) {
            toast.success(resp.message || "Profile updated successfully");
            onCompleted(resp.data && resp.data.employee);
            return;
        }
        toast.error((resp && resp.message) || "Could not save your profile. Please try again.");
    };

    return (
        <Dialog open={open} disableEscapeKeyDown fullWidth maxWidth="sm"
            className="wh-dialog" aria-labelledby="profile-setup-title">

            <div className="wh-dialog-head">
                <div className="wh-dialog-headings">
                    <h5 id="profile-setup-title">Complete your profile</h5>
                    <p>A few details before you get started. All of them are required.</p>
                </div>
            </div>

            <DialogContent>
                <h6 className="wh-section-title" style={{ marginTop: 0 }}>
                    <BadgeOutlinedIcon />Your details
                </h6>
                <div className="wh-facts-grid wh-facts-stack">
                    <Fact icon={<BadgeOutlinedIcon />} label="Name" value={user.employeeName} />
                    <Fact icon={<AlternateEmailOutlinedIcon />} label="Email" value={user.emailID} />
                    <Fact icon={<SupervisorAccountOutlinedIcon />} label="Manager" value={user.managerName} />
                </div>

                <h6 className="wh-section-title">
                    <TimelineOutlinedIcon />Experience
                </h6>
                <TextField
                    fullWidth
                    required
                    size="small"
                    label="Total experience"
                    placeholder="e.g. 3 Years"
                    value={experience}
                    onChange={(e) => { setExperience(e.target.value); clearError('experience'); }}
                    error={Boolean(errors.experience)}
                    helperText={errors.experience || " "}
                />

                <h6 className="wh-section-title">
                    <PsychologyOutlinedIcon />Current skills
                </h6>
                <div className="wh-form-grid">
                    <TextField
                        size="small"
                        label="Skill name"
                        value={newSkill.name}
                        onChange={(e) => { setNewSkill({ ...newSkill, name: e.target.value }); clearError('skills'); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
                    />
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="setup-skill-level">Level</InputLabel>
                            <Select
                                labelId="setup-skill-level"
                                label="Level"
                                value={newSkill.level}
                                onChange={(e) => { setNewSkill({ ...newSkill, level: e.target.value }); clearError('skills'); }}
                            >
                                {skillLevels.map(level =>
                                    <MenuItem key={level} value={level}>{level}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Tooltip title="Add skill" placement="top" arrow>
                            <span>
                                <IconButton className="wh-add-icon" aria-label="Add skill" onClick={addSkill}>
                                    <AddRoundedIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                </div>

                {skills.length > 0
                    ? <div className="wh-pills">
                        {skills.map((skill, index) =>
                            <span className="wh-pill wh-pill-removable" key={`${skill.name}-${index}`}>
                                {skill.name} · {skill.level}
                                <Tooltip title="Remove" placement="top" arrow>
                                    <button type="button" aria-label={`Remove ${skill.name}`}
                                        onClick={() => removeSkill(index)}>
                                        <ClearIcon />
                                    </button>
                                </Tooltip>
                            </span>
                        )}
                    </div>
                    : <p className="wh-section-empty">No skills added yet.</p>}
                {errors.skills ? <p className="wh-field-error">{errors.skills}</p> : null}

                <h6 className="wh-section-title">
                    <AutoAwesomeOutlinedIcon />Desired skills
                </h6>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Skill you want to learn"
                        value={newDesiredSkill}
                        onChange={(e) => { setNewDesiredSkill(e.target.value); clearError('desiredSkills'); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDesiredSkill(); } }}
                    />
                    <Tooltip title="Add desired skill" placement="top" arrow>
                        <span>
                            <IconButton className="wh-add-icon" aria-label="Add desired skill" onClick={addDesiredSkill}>
                                <AddRoundedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>

                {desiredSkills.length > 0
                    ? <div className="wh-pills">
                        {desiredSkills.map((skill, index) =>
                            <span className="wh-pill wh-pill-removable" key={`${skill}-${index}`}>
                                {skill}
                                <Tooltip title="Remove" placement="top" arrow>
                                    <button type="button" aria-label={`Remove ${skill}`}
                                        onClick={() => removeDesiredSkill(skill)}>
                                        <ClearIcon />
                                    </button>
                                </Tooltip>
                            </span>
                        )}
                    </div>
                    : <p className="wh-section-empty">No desired skills added yet.</p>}
                {errors.desiredSkills ? <p className="wh-field-error">{errors.desiredSkills}</p> : null}
            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={submit} disabled={saving}>
                    {saving ? "Saving..." : "Save & Continue"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProfileSetupModal;
